import { useTheme } from '../../context/theme';

export function Dots() {
  return (
    <div style={{ display: 'flex', gap: 5, justifyContent: 'center', padding: '20px 0' }}>
      {[0, 1, 2].map(i => (
        <div key={i} style={{ width: 6, height: 6, borderRadius: '50%', background: 'rgba(255,255,255,0.58)', animation: `dlpp 1.2s ease ${i * 0.2}s infinite` }} />
      ))}
    </div>
  );
}

export function Skeleton({ width, height, borderRadius, style }) {
  const { theme } = useTheme();
  return <div className="skeleton-shimmer" style={{ width: width || '100%', height: height || 16, borderRadius: borderRadius || 6, background: theme.bgHover, ...(style || {}) }} />;
}

export function SkeletonCard({ style }) {
  const { theme } = useTheme();
  return (
    <div style={{ background: theme.bgCard, border: `1px solid ${theme.border}`, borderRadius: 14, padding: 20, boxShadow: theme.shadow, ...(style || {}) }}>
      <Skeleton width="60%" height={14} style={{ marginBottom: 12 }} />
      <Skeleton width="90%" height={10} style={{ marginBottom: 8 }} />
      <Skeleton width="75%" height={10} style={{ marginBottom: 14 }} />
      <div style={{ display: 'flex', gap: 8 }}>
        <Skeleton width={60} height={28} borderRadius={8} />
        <Skeleton width={60} height={28} borderRadius={8} />
      </div>
    </div>
  );
}

export function SkeletonTable({ rows = 5, cols = 4 }) {
  const { theme } = useTheme();
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ borderBottom: `2px solid ${theme.border}` }}>
            {Array.from({ length: cols }).map((_, i) => <th key={i} style={{ padding: '8px 10px', textAlign: 'left' }}><Skeleton width="70%" height={12} /></th>)}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }).map((_, ri) => (
            <tr key={ri} style={{ borderBottom: `1px solid ${theme.border}` }}>
              {Array.from({ length: cols }).map((_, ci) => <td key={ci} style={{ padding: 10 }}><Skeleton width={`${60 + Math.random() * 30}%`} height={10} /></td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function SkeletonList({ count = 5, style }) {
  const { theme } = useTheme();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, ...(style || {}) }}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: `1px solid ${theme.border}` }}>
          <Skeleton width={32} height={32} borderRadius={8} style={{ flexShrink: 0 }} />
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <Skeleton width={`${50 + Math.random() * 30}%`} height={12} />
            <Skeleton width={`${30 + Math.random() * 40}%`} height={9} />
          </div>
          <Skeleton width={50} height={10} />
        </div>
      ))}
    </div>
  );
}

export function SkeletonStatCards() {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: 12, marginBottom: 16 }}>
      {Array.from({ length: 4 }).map((_, i) => <SkeletonCard key={i} style={{ padding: 16 }} />)}
    </div>
  );
}

export function SkeletonDashboard() {
  const { theme } = useTheme();
  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: 12, marginBottom: 20 }}>
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} style={{ background: theme.bgCard, border: `1px solid ${theme.border}`, borderRadius: 14, padding: 20 }}>
            <Skeleton width="40%" height={10} style={{ marginBottom: 10 }} />
            <Skeleton width="60%" height={24} style={{ marginBottom: 6 }} />
            <Skeleton width="30%" height={10} />
          </div>
        ))}
      </div>
      <div style={{ background: theme.bgCard, border: `1px solid ${theme.border}`, borderRadius: 14, padding: 20, marginBottom: 20 }}>
        <Skeleton width="30%" height={14} style={{ marginBottom: 16 }} />
        <Skeleton width="100%" height={160} borderRadius={8} />
      </div>
      <div style={{ background: theme.bgCard, border: `1px solid ${theme.border}`, borderRadius: 14, padding: 20 }}>
        <Skeleton width="25%" height={14} style={{ marginBottom: 12 }} />
        <SkeletonList count={4} />
      </div>
    </div>
  );
}

export function SkeletonKanban() {
  const { theme } = useTheme();
  return (
    <div style={{ display: 'flex', gap: 16, overflowX: 'auto', padding: '4px 0' }}>
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} style={{ minWidth: 260, background: theme.bgHover, borderRadius: 12, padding: 14 }}>
          <Skeleton width="50%" height={14} style={{ marginBottom: 14 }} />
          {Array.from({ length: 2 + Math.floor(Math.random() * 2) }).map((_, j) => <SkeletonCard key={j} style={{ marginBottom: 10 }} />)}
        </div>
      ))}
    </div>
  );
}

export function SkeletonCalendar() {
  const { theme } = useTheme();
  return (
    <div style={{ background: theme.bgCard, border: `1px solid ${theme.border}`, borderRadius: 14, padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Skeleton width={120} height={20} />
        <div style={{ display: 'flex', gap: 8 }}>
          <Skeleton width={32} height={32} borderRadius={8} />
          <Skeleton width={32} height={32} borderRadius={8} />
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 2 }}>
        {Array.from({ length: 7 }).map((_, i) => <div key={`h${i}`} style={{ padding: 8, textAlign: 'center' }}><Skeleton width="60%" height={10} style={{ margin: '0 auto' }} /></div>)}
        {Array.from({ length: 35 }).map((_, i) => (
          <div key={i} style={{ padding: 8, minHeight: 60, border: `1px solid ${theme.border}`, borderRadius: 4 }}>
            <Skeleton width={20} height={12} style={{ marginBottom: 4 }} />
            {i % 7 < 2 && <Skeleton width="80%" height={8} style={{ marginTop: 4 }} />}
          </div>
        ))}
      </div>
    </div>
  );
}

export function SkeletonMessages() {
  const { theme } = useTheme();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 16 }}>
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} style={{ display: 'flex', gap: 10, alignItems: i % 2 ? 'flex-end' : 'flex-start', flexDirection: i % 2 ? 'row-reverse' : 'row' }}>
          <Skeleton width={32} height={32} borderRadius="50%" style={{ flexShrink: 0 }} />
          <div style={{ maxWidth: '60%', display: 'flex', flexDirection: 'column', gap: 4 }}>
            <Skeleton width={80 + Math.random() * 120} height={10} />
            <Skeleton width={120 + Math.random() * 100} height={32} borderRadius={12} />
          </div>
        </div>
      ))}
    </div>
  );
}

export function SkeletonDocGrid() {
  const { theme } = useTheme();
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(200px,1fr))', gap: 14 }}>
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} style={{ background: theme.bgCard, border: `1px solid ${theme.border}`, borderRadius: 12, padding: 16 }}>
          <Skeleton width={40} height={40} borderRadius={8} style={{ marginBottom: 12 }} />
          <Skeleton width="70%" height={12} style={{ marginBottom: 8 }} />
          <Skeleton width="50%" height={10} />
        </div>
      ))}
    </div>
  );
}
